import { useState, useEffect } from 'react';
import { Subscription } from 'rxjs';
import { v4 } from 'uuid';
import { EventBusSrv } from '@grafana/data';
import { SceneComponentWrapper } from './SceneComponentWrapper.js';
import { SceneObjectStateChangedEvent } from './events.js';
import { cloneSceneObject } from './utils.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
class SceneObjectBase {
  constructor(state) {
    this._isActive = false;
    this._events = new EventBusSrv();
    this._activationHandlers = [];
    this._deactivationHandlers = [];
    this._subs = new Subscription();
    this._refCount = 0;
    if (!state.key) {
      state.key = v4();
    }
    this._state = Object.freeze(state);
    this._setParent();
  }
  get state() {
    return this._state;
  }
  get isActive() {
    return this._isActive;
  }
  get parent() {
    return this._parent;
  }
  get variableDependency() {
    return this._variableDependency;
  }
  get urlSync() {
    return this._urlSync;
  }
  get Component() {
    return SceneComponentWrapper;
  }
  _setParent() {
    this.forEachChild((child) => {
      child._parent = this;
    });
  }
  subscribeToState(handler) {
    return this._events.subscribe(SceneObjectStateChangedEvent, (event) => {
      if (event.payload.changedObject === this) {
        handler(event.payload.newState, event.payload.prevState);
      }
    });
  }
  subscribeToEvent(eventType, handler) {
    return this._events.subscribe(eventType, handler);
  }
  setState(update) {
    const prevState = this._state;
    const newState = __spreadValues(__spreadValues({}, this._state), update);
    this._state = Object.freeze(newState);
    this._setParent();
    this.publishEvent(
      new SceneObjectStateChangedEvent({
        prevState,
        newState,
        partialUpdate: update,
        changedObject: this
      }),
      true
    );
  }
  publishEvent(event, bubble) {
    this._events.publish(event);
    if (bubble && this.parent) {
      this.parent.publishEvent(event, bubble);
    }
  }
  getRoot() {
    return !this._parent ? this : this._parent.getRoot();
  }
  _internalActivate() {
    this._isActive = true;
    const { $data, $variables, $timeRange, $behaviors } = this.state;
    if ($timeRange && !$timeRange.isActive) {
      this._deactivationHandlers.push($timeRange.activate());
    }
    if ($variables && !$variables.isActive) {
      this._deactivationHandlers.push($variables.activate());
    }
    if ($data && !$data.isActive) {
      this._deactivationHandlers.push($data.activate());
    }
    if ($behaviors) {
      for (const behavior of $behaviors) {
        if (behavior instanceof SceneObjectBase) {
          this._deactivationHandlers.push(behavior.activate());
        } else if (typeof behavior === "function") {
          const deactivationHandler = behavior(this);
          if (deactivationHandler) {
            this._deactivationHandlers.push();
          }
        }
      }
    }
    this._activationHandlers.forEach((handler) => {
      const result = handler();
      if (result) {
        this._deactivationHandlers.push(result);
      }
    });
  }
  activate() {
    if (!this.isActive) {
      this._internalActivate();
    }
    this._refCount++;
    let called = false;
    return () => {
      this._refCount--;
      if (called) {
        const msg = `SceneObject cancelation handler returned by activate() called a second time`;
        console.error(msg, this);
        throw new Error(msg);
      }
      called = true;
      if (this._refCount === 0) {
        this._internalDeactivate();
      }
    };
  }
  _internalDeactivate() {
    this._isActive = false;
    this._deactivationHandlers.forEach((handler) => handler());
    this._deactivationHandlers = [];
    this._events.removeAllListeners();
    this._subs.unsubscribe();
    this._subs = new Subscription();
  }
  useState() {
    return useSceneObjectState(this);
  }
  forceRender() {
    this.setState({});
  }
  clone(withState) {
    return cloneSceneObject(this, withState);
  }
  addActivationHandler(handler) {
    this._activationHandlers.push(handler);
  }
  forEachChild(callback) {
    for (const propValue of Object.values(this.state)) {
      if (propValue instanceof SceneObjectBase) {
        callback(propValue);
      }
      if (Array.isArray(propValue)) {
        for (const child of propValue) {
          if (child instanceof SceneObjectBase) {
            callback(child);
          }
        }
      }
    }
  }
}
function useSceneObjectState(model) {
  const [_, setState] = useState(model.state);
  const stateAtFirstRender = model.state;
  useEffect(() => {
    const s = model.subscribeToState(setState);
    if (model.state !== stateAtFirstRender) {
      setState(model.state);
    }
    return () => s.unsubscribe();
  }, [model]);
  return model.state;
}

export { SceneObjectBase };
//# sourceMappingURL=SceneObjectBase.js.map
